import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
// biome-ignore lint/style/useNodejsImportProtocol: webpack uses path-browserify fallback
import path from 'path'
import { useState } from 'react'

import useOnPremAgentClient from '~/api/OnPremAgentClient/useOnPremAgentClient'
import type { GetRuntimeResponse } from '~/api/onPremAgent/cronjobAPI'
import type { MissedDataset } from '~/api/onPremAgent/datasetsAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import { FullPageError } from '~/components/Errors'
import LoadingMessage from '~/components/LoadingMessage'
import Table, { type TableColumn } from '~/components/Table'
import Toaster from '~/components/Toaster'
import { getInstrumentImage } from '~/utils/instrument'
import DatasetBreadcrumbs from '../DatasetBreadcrumbs'
import cs from './missed_datasets_page.scss'

export default function MissedDatasetsPage() {
  const client = useOnPremAgentClient()
  const [actionsDisabled, setActionsDisabled] = useState(false)
  const {
    data,
    error,
    isLoading: isLoadingDatasets,
    refetch: refetchMissedDatasets,
  } = useQuery({
    queryKey: [QUERY_KEYS.MISSED_DATASETS],
    queryFn: async () => client.datasets.listMissedDatasets(),
  })
  const {
    data: datasetVerificationRuntime,
    error: runtimeError,
    isLoading: isLoadingRuntime,
  } = useQuery<GetRuntimeResponse>({
    queryKey: [QUERY_KEYS.MISSED_DATASETS_RUNTIME],
    queryFn: async () => client.cron.getRuntimeDatasetVerification(),
  })
  const {
    data: nextDatasetVerificationRuntime,
    error: nextRuntimeError,
    isLoading: isLoadingNextRuntime,
  } = useQuery<GetRuntimeResponse>({
    queryKey: [QUERY_KEYS.MISSED_DATASETS_RUNTIME, 'next'],
    queryFn: async () => client.cron.getNextRuntimeDatasetVerification(),
  })

  const { mutate: scheduleDatasetVerification, isPending: isSchedulingVerification } =
    useMutation({
      mutationFn: async () => client.cron.scheduleDatasetVerification(),
      onSuccess: () => {
        Toaster.show({
          message: 'Dataset verification scheduled successfully.',
          intent: 'success',
        })
        refetchMissedDatasets()
      },
      onError: () => {
        Toaster.show({
          message: 'Failed to schedule dataset verification.',
          intent: 'danger',
        })
      },
    })

  const uploadDataset = async (row: MissedDataset) => {
    if (!row.data_ingestor_uuid) {
      Toaster.show({
        message: 'Cannot upload: missing data ingestor UUID.',
        intent: 'danger',
      })
      return
    }
    if (!row.filepath) {
      Toaster.show({
        message: 'Cannot upload: missing dataset filepath.',
        intent: 'danger',
      })
      return
    }
    let directory_or_file_path = ''
    switch (row.instrument_type) {
      case 'cytation':
        directory_or_file_path = row.filepath
        break
      case 'imagexpress_micro_confocal':
        directory_or_file_path = path.dirname(row.filepath)
        break
    }
    setActionsDisabled(true)
    try {
      Toaster.show({
        message: `Uploading dataset for dataset ${row.name}.`,
        intent: 'primary',
      })
      await client.dataIngestors.uploadDataset(row.data_ingestor_uuid, {
        directory_or_file_path,
      })
      await client.datasets.updateMissedDataset({
        dataset_name: row.name,
        data_ingestor_uuid: row.data_ingestor_uuid,
        uploaded: true,
        is_delete: true,
      })
      await refetchMissedDatasets()
      Toaster.show({
        message: `Uploaded new dataset: ${row.name}`,
        intent: 'success',
      })
    } catch (_e) {
      Toaster.show({
        message: `Failed to upload dataset: ${row.name}`,
        intent: 'danger',
      })
    } finally {
      setActionsDisabled(false)
    }
  }

  const rejectDataset = async (row: MissedDataset) => {
    if (!row.data_ingestor_uuid) {
      Toaster.show({
        message: 'Cannot reject: missing data ingestor UUID.',
        intent: 'danger',
      })
      return
    }
    setActionsDisabled(true)
    try {
      await client.datasets.updateMissedDataset({
        dataset_name: row.name,
        data_ingestor_uuid: row.data_ingestor_uuid,
        uploaded: row.uploaded,
        is_delete: true,
      })
      await refetchMissedDatasets()
      Toaster.show({
        message: 'Ignored dataset.',
        intent: 'success',
      })
    } catch (_e) {
      Toaster.show({
        message: 'Failed to ignore dataset.',
        intent: 'danger',
      })
    } finally {
      setActionsDisabled(false)
    }
  }

  const renderDetectedAt = (row: MissedDataset) => {
    return row.detected_at ? dayjs(row.detected_at).fromNow() : '—'
  }

  const renderInstrument = (row: MissedDataset) => {
    if (!row.instrument_name || !row.instrument_type) {
      return <div>—</div>
    }
    return (
      <div className={cs.instrumentNameContainer}>
        <img
          alt={row.instrument_name}
          className={cs.instrumentImage}
          src={getInstrumentImage({
            instrumentType: row.instrument_type,
            instrumentName: row.instrument_name,
          })}
        />
        <span className={cs.instrumentName}>{row.instrument_name}</span>
      </div>
    )
  }

  const renderActions = (row: MissedDataset) => {
    if (!row.uploaded) {
      return (
        <div className='flex items-center gap-12'>
          <Button
            label={'Upload'}
            variant={'minimal'}
            disabled={actionsDisabled}
            onClick={() => uploadDataset(row)}
          />
          <Button
            label={'Ignore'}
            variant={'minimal'}
            disabled={actionsDisabled}
            onClick={() => rejectDataset(row)}
          />
        </div>
      )
    }
  }

  const tableColumns: TableColumn<MissedDataset>[] = [
    {
      name: 'Dataset Name',
      width: 200,
      render: (row: MissedDataset) => row.name || '—',
      oneLine: true,
    },
    {
      name: 'File Path',
      width: 300,
      render: (row: MissedDataset) => row.filepath || '—',
      oneLine: true,
      smallText: true,
    },
    {
      name: 'Instrument',
      width: 200,
      render: renderInstrument,
      oneLine: true,
    },
    {
      name: 'Detected Time',
      width: 200,
      render: (row: MissedDataset) => renderDetectedAt(row),
      oneLine: true,
      smallText: true,
    },
    {
      name: 'Actions',
      width: 100,
      render: renderActions,
    },
  ]

  const tableData = data?.map((dataset, index) => ({
    ...dataset,
    key: `${dataset.filepath}-${dataset.detected_at ?? index}`,
  }))

  const formatRuntime = (runtime?: string | null) =>
    runtime ? dayjs(runtime).format('MMM D, YYYY h:mm A') : null

  const lastRuntime = formatRuntime(datasetVerificationRuntime?.last_executed_time)
  const nextRuntime = formatRuntime(
    nextDatasetVerificationRuntime?.next_executed_time ??
      nextDatasetVerificationRuntime?.last_executed_time,
  )
  const runtimeDisplay = (() => {
    if (isLoadingRuntime) {
      return 'Loading...'
    }
    if (runtimeError) {
      return 'Unavailable'
    }
    if (!lastRuntime) {
      return '—'
    }
    return lastRuntime
  })()

  const nextRuntimeDisplay = (() => {
    if (isLoadingNextRuntime) {
      return 'Loading...'
    }
    if (nextRuntimeError) {
      return 'Unavailable'
    }
    if (!nextRuntime) {
      return '—'
    }
    return nextRuntime
  })()

  const [runtimeMode, setRuntimeMode] = useState<'last' | 'next'>('last')
  const runtimeLabel = runtimeMode === 'last' ? 'Last scan:' : 'Next scan:'
  const runtimeValue = runtimeMode === 'last' ? runtimeDisplay : nextRuntimeDisplay
  const toggleRuntimeMode = () => {
    setRuntimeMode(prev => (prev === 'last' ? 'next' : 'last'))
  }

  if (error) {
    return <FullPageError error={error as Error} />
  }

  return (
    <div className={cs.missedDatasetsPage}>
      <div className={cs.topBar}>
        <div className={cs.controls}>
          <DatasetBreadcrumbs currentPageLabel='Review Missed Datasets' />
          <div className={cs.spacer} />
          <div className='mr-2'>
            <Button
              type='primary'
              label='Schedule Now'
              onClick={() => scheduleDatasetVerification()}
              disabled={isSchedulingVerification}
            />
          </div>
          <div className={cs.lastRuntime}>
            <button
              type='button'
              className={cs.runtimeToggle}
              onClick={toggleRuntimeMode}
            >
              <span>{runtimeLabel}</span>
              <span>{runtimeValue}</span>
            </button>
          </div>
        </div>
      </div>
      {isLoadingDatasets && (
        <LoadingMessage
          className={cs.loadingMessage}
          label='Loading missed datasets...'
        />
      )}
      {!isLoadingDatasets && (
        <div className={cs.content}>
          <div className={cs.tableWrapper}>
            <Table<MissedDataset>
              columns={tableColumns}
              data={tableData ?? []}
              rowKey='key'
              noDataMessage='No missed datasets found.'
              rowLeftRightPaddingVariant='rowPaddingNormal'
              headerTopBottomPaddingVariant='headerTopBottomPaddingEqual'
              heightSizing='default'
              stickyHeaders
            />
          </div>
        </div>
      )}
    </div>
  )
}
